import {
  Address,
  AuthFlowKind,
  Capabilities,
  Client,
  Keypair,
  Pubky,
  PublicKey,
  resolvePubky,
  Session,
  Signer,
} from '@synonymdev/pubky';
import type { TKeypairParams } from '@/application/auth/auth.types';
import {
  getDefaultHttpRelay,
  getHomeserver,
  getHomeserverUrl,
  getPkarrRelays,
  getTestnet,
  HOMESERVER_CAPABILITIES,
  isStagingHomeserverDeploy,
} from '@/config/network';
import { AppError } from '@/libs/error/error';
import { AuthErrorCode, ServerErrorCode, ValidationErrorCode } from '@/libs/error/error.codes';
import { Err } from '@/libs/error/error.factories';
import { httpResponseToError } from '@/libs/error/error.http';
import { ErrorCategory, ErrorService } from '@/libs/error/error.types';
import { hasHttpStatus } from '@/libs/error/error.utils';
import { HttpMethod, HttpStatusCode } from '@/libs/http/http.types';
import { Identity } from '@/libs/identity/identity';
import { Logger } from '@/libs/logger/logger';
import { HOMESERVER_EVENT_STREAM_SUBSCRIBE_OPERATION } from '@/libs/observability/sentry.constants';
import { sleep } from '@/libs/utils/utils';
import type { Pubky as TPubkyModel } from '@/models/models.types';
import type {
  TGenerateAuthUrlResult,
  THomeserverRestoreSessionParams,
  THomeserverSessionResult,
  THomeserverSignUpParams,
  TSignupTokenVerificationStatus,
} from '@/services/homeserver/homeserver.types';
import { useAuthStore } from '@/stores/auth/auth.store';
import { extractStatusCode, handleError } from './error.utils';
import type {
  PubPath,
  TGenerateSignupAuthUrlParams,
  THomeserverBytesResult,
  THomeserverFetchParams,
  THomeserverListAllParams,
  THomeserverListParams,
  THomeserverPublicKeyParams,
  THomeserverRequestParams,
  THomeserverUserEvent,
  TOwnedSessionPath,
  TPutBlobParams,
} from './homeserver.types';
import {
  assertOk,
  createCancelableAuthApproval,
  getOwnedResponse,
  isHttpUrl,
  parseResponseOrUndefined,
  PUBKY_PREFIX,
  resolveOwnedSessionPath,
} from './homeserver.utils';

const STORAGE_PATH_PREFIXES = ['/pub/', '/priv/'] as const;
const DELETE_IDEMPOTENT_MAX_ATTEMPTS = 3;
const DELETE_IDEMPOTENT_RETRY_DELAY_MS = 500;
/** Default limit for list operations */
const LIST_DEFAULT_LIMIT = 500;

type HomeserverSdkUserEvent = THomeserverUserEvent & {
  free(): void;
};

export class HomeserverService {
  private constructor() {}

  private static pubkySdk: Pubky | null = null;

  /**
   * Gets the Pubky SDK singleton.
   */
  private static getPubkySdk(): Pubky {
    if (!this.pubkySdk) {
      if (getTestnet()) {
        this.pubkySdk = Pubky.testnet();
      } else {
        const client = new Client({ pkarr: { relays: getPkarrRelays() } });
        this.pubkySdk = Pubky.withClient(client);
      }
    }
    return this.pubkySdk;
  }

  private static resolveOwnedSessionPath(url: string): TOwnedSessionPath | null {
    const session = useAuthStore.getState().selectSession();
    return resolveOwnedSessionPath({ url, session, allowedPrefixes: STORAGE_PATH_PREFIXES });
  }

  /**
   * Gets a signer for the homeserver
   * @param keypair - The keypair to get a signer for
   * @returns The signer
   */
  private static getSigner(keypair: Keypair): Signer {
    const pubkySdk = this.getPubkySdk();
    return pubkySdk.signer(keypair);
  }

  /**
   * Resolves the key's homeserver from its PKARR record.
   * @returns The homeserver public key, or `null` when the record is provably absent
   * @throws When the lookup itself failed (relay/network error) — absence NOT proven.
   *   The SDK rejects with `PkarrError`, which is mapped to a retryable Network error.
   */
  private static async resolveHomeserverRecord({ publicKey }: THomeserverPublicKeyParams) {
    try {
      const pubkySdk = this.getPubkySdk();
      const homeserver = await pubkySdk.getHomeserverOf(publicKey);
      return homeserver ?? null;
    } catch (error) {
      return handleError({
        error,
        additionalContext: { publicKey: publicKey?.z32?.(), operation: 'resolveHomeserverRecord' },
      });
    }
  }

  /**
   * Resolve PKARR homeserver; on staging, require it matches this deploy's homeserver.
   *
   * An absent record is rejected the same way as a mismatched one: absence cannot
   * prove the key belongs here, both outcomes are deterministic (retrying the
   * lookup cannot change them), and fail-open would re-enable the prod-key
   * force-republish this guard exists to prevent (#2126). This also means a key
   * whose just-published record has not yet propagated to our relays is rejected
   * until it propagates. Only a lookup that itself failed (relay/network error)
   * throws a retryable error instead (Network for the SDK's `PkarrError`,
   * Server for anything else).
   */
  static async assertUserHomeserverAllowed({ publicKey }: THomeserverPublicKeyParams): Promise<void> {
    if (!isStagingHomeserverDeploy()) {
      return;
    }

    const homeserver = await this.resolveHomeserverRecord({ publicKey });
    const configuredHomeserver = getHomeserver();
    const resolvedHomeserver = homeserver ? homeserver.z32() : null;
    if (resolvedHomeserver !== configuredHomeserver) {
      const context = {
        configuredHomeserver,
        resolvedHomeserver,
        publicKey: publicKey.z32(),
      };
      // An expected user mistake (prod key on a staging deploy), not a system
      // fault — constructed directly rather than via the Err factory so every
      // occurrence does not emit an error log plus a Sentry error event.
      Logger.info('Rejected sign-in: key is not linked to this staging homeserver', context);
      throw new AppError({
        category: ErrorCategory.Auth,
        code: AuthErrorCode.WRONG_ENVIRONMENT_HOMESERVER,
        message: 'This key is not linked to this staging deploy homeserver.',
        service: ErrorService.Homeserver,
        operation: 'assertUserHomeserverAllowed',
        context,
      });
    }
  }

  /**
   * Signs up a new user in the homeserver
   * @param keypair - The keypair to sign up with
   * @param signupToken - The signup token to use
   * @returns The session
   */
  static async signUp({ keypair, signupToken }: THomeserverSignUpParams): Promise<THomeserverSessionResult> {
    try {
      const homeserverPublicKey = PublicKey.from(getHomeserver());
      const signer = this.getSigner(keypair);
      // Cookie-backed session on purpose: the grant-auth migration is tracked separately.
      const session = await signer.signupCookie(homeserverPublicKey, signupToken);

      Logger.debug('Signup successful', { session });

      return { session };
    } catch (error) {
      return handleError({
        error,
        additionalContext: { signupTokenProvided: Boolean(signupToken), operation: 'signUp' },
        statusCode: HttpStatusCode.INTERNAL_SERVER_ERROR,
        alwaysUseHomeserverError: true,
      });
    }
  }

  /**
   * Verifies a signup token (invite code) against the homeserver.
   *
   * Performs a GET to the homeserver's `/signup_tokens/<token>` endpoint. This is a
   * homeserver-root endpoint that cannot be reached via the homeserver pubkey (its PKARR
   * record has no resolvable HTTPS endpoint), so the explicit {@link getHomeserverUrl} is used.
   *
   * A definitive homeserver response distinguishes valid, used, and unknown tokens, whereas
   * a failure to reach the homeserver is surfaced as a thrown error so callers can tell
   * verification outcomes apart from "couldn't verify right now".
   *
   * @param signupToken - The signup token / invite code to verify
   * @returns `'valid'` when the token exists and is unused, `'used'` when already redeemed,
   *   `'invalid'` when the homeserver does not recognise the token (404).
   * @throws When the homeserver could not be reached (network error, timeout, DNS/PKARR failure)
   *   or returns an unexpected status.
   */
  static async verifySignupToken(signupToken: string): Promise<TSignupTokenVerificationStatus> {
    const url = `${getHomeserverUrl()}/signup_tokens/${encodeURIComponent(signupToken)}`;
    try {
      const response = await this.getPubkySdk().client.fetch(url, { method: HttpMethod.GET });
      Logger.debug('Signup token verification response', { status: response.status });

      if (response.status === HttpStatusCode.NOT_FOUND) {
        return 'invalid';
      }

      if (!response.ok) {
        throw new Error(`Signup token verification failed with status ${response.status}`);
      }

      const data = (await response.json()) as { status?: string };
      if (data.status === 'valid') {
        return 'valid';
      }
      if (data.status === 'used') {
        return 'used';
      }

      Logger.warn('Unexpected signup token verification payload', { status: data.status });
      return 'invalid';
    } catch (error) {
      Logger.warn('Signup token verification could not reach the homeserver', { error });
      throw error;
    }
  }

  /**
   * Signs in a user to the homeserver.
   *
   * If signin fails due to missing homeserver records, this method will attempt to republish
   * the homeserver and return `undefined` to signal the caller should retry the signin.
   *
   * @param keypair - The keypair to sign in with
   * @returns The session result, or `undefined` if homeserver was republished and caller should retry
   */
  static async signIn({ keypair }: TKeypairParams): Promise<THomeserverSessionResult | undefined> {
    const signer = this.getSigner(keypair);

    if (isStagingHomeserverDeploy()) {
      // Fail closed on staging: an inconclusive PKARR lookup must never migrate an
      // unverified key to the configured staging homeserver.
      await this.assertUserHomeserverAllowed({ publicKey: keypair.publicKey });
    } else {
      // Lookup-failure hardening: self-heal (republish) only a PROVABLY ABSENT
      // record (e.g. expired from the DHT). A failed lookup throws instead —
      // republishing on a transient error could overwrite an existing record
      // that points at another homeserver. NOTE: the signin-failure branch
      // below still republishes a PRESENT record (long-standing stale-record
      // migration self-heal); narrowing that is a separate product decision.
      const homeserverRecord = await this.resolveHomeserverRecord({ publicKey: keypair.publicKey });
      if (homeserverRecord === null) {
        return await this.republishConfiguredHomeserver({
          signer,
          keypair,
          originalError: 'homeserver record absent from PKARR',
        });
      }
    }

    try {
      // Cookie-backed session on purpose: the grant-auth migration is tracked separately.
      const session = await signer.signinCookie();
      return { session };
    } catch (signinError) {
      return await this.republishConfiguredHomeserver({ signer, keypair, originalError: signinError });
    }
  }

  private static async republishConfiguredHomeserver({
    signer,
    keypair,
    originalError,
  }: {
    signer: Signer;
    keypair: Keypair;
    originalError: unknown;
  }): Promise<undefined> {
    try {
      const homeserverPublicKey = PublicKey.from(getHomeserver());
      await signer.pkdns.publishHomeserverForce(homeserverPublicKey);
      Logger.debug('Republish homeserver successful', { keypair: Identity.pubkyFromKeypair(keypair) });
      return undefined;
    } catch (republishError) {
      return handleError({
        error: republishError,
        additionalContext: {
          pubky: Identity.pubkyFromKeypair(keypair),
          originalSigninError: String(originalError),
          operation: 'republishConfiguredHomeserver',
        },
        statusCode: HttpStatusCode.UNAUTHORIZED,
      });
    }
  }

  /**
   * Generates an authentication URL for the homeserver
   * @param caps - The capabilities to use
   * @returns The authentication URL and approval promise
   */
  static async generateAuthUrl(caps?: Capabilities): Promise<TGenerateAuthUrlResult> {
    const capabilities: Capabilities = caps || HOMESERVER_CAPABILITIES;

    try {
      const pubkySdk = this.getPubkySdk();
      // Cookie auth flow on purpose: the grant-auth migration is tracked separately.
      const flow = pubkySdk.startCookieAuthFlow(capabilities, AuthFlowKind.signin(), getDefaultHttpRelay());
      const approval = createCancelableAuthApproval(flow);

      return {
        authorizationUrl: flow.authorizationUrl,
        awaitApproval: approval.awaitApproval,
        cancelAuthFlow: approval.cancel,
      };
    } catch (error) {
      return handleError({ error, additionalContext: { capabilities, relay: getDefaultHttpRelay() } });
    }
  }

  /**
   * Generates an authentication signup URL for the homeserver.
   *
   * Temporary hack to create a signup deeplink from the signin url still using the old pubky sdk.
   * The new sdk will handle the creation of the signup deeplink out of the box.
   * But until then, we need to use this hack.
   * @param inviteCode InviteCode to the homeserver
   * @param caps - The capabilities to use
   * @returns The authentication URL and approval promise
   */
  static async generateSignupAuthUrl({
    inviteCode,
    caps,
  }: TGenerateSignupAuthUrlParams): Promise<TGenerateAuthUrlResult> {
    const res = await this.generateAuthUrl(caps);
    const url = URL.parse(res.authorizationUrl);
    if (!url) {
      throw Err.validation(ValidationErrorCode.INVALID_INPUT, 'Invalid authorization URL format', {
        service: ErrorService.Homeserver,
        operation: 'generateSignupAuthUrl',
        context: { authorizationUrl: res.authorizationUrl },
      });
    }
    url.host = 'signup';
    url.pathname = '';
    url.searchParams.set('hs', getHomeserver());
    url.searchParams.set('st', inviteCode);
    res.authorizationUrl = url.toString();
    return res;
  }

  /**
   * Logs out a user from the homeserver
   * @param session - The authenticated Session to sign out
   * @returns Void
   */
  static async logout({ session }: THomeserverSessionResult) {
    try {
      await session.signout();
    } catch (error) {
      return handleError({ error, additionalContext: { url: 'signout' } });
    }
  }

  private static async fetch({ url, options }: THomeserverFetchParams): Promise<Response> {
    try {
      const pubkySdk = this.getPubkySdk();
      const httpBridge = pubkySdk.client;
      // Resolve pubky identifiers to transport URLs before fetching
      const resolvedUrl = url.startsWith(PUBKY_PREFIX) ? resolvePubky(url) : url;
      const response = await httpBridge.fetch(resolvedUrl, {
        method: options?.method,
        body: options?.body as BodyInit | undefined,
        credentials: 'include',
      });

      Logger.debug('Response from homeserver', { response });

      return response;
    } catch (error) {
      return handleError({ error, additionalContext: { url, method: options?.method } });
    }
  }

  /**
   * Performs a request against the homeserver.
   *
   * Sends a JSON payload when provided and throws if the response is not OK.
   * Note: Under the hood this uses `fetch` with `credentials: 'include'`.
   *
   * @param {HttpMethod} method - HTTP method to use (e.g. PUT, POST, DELETE).
   * @param {string} url - Pubky URL.
   * @param {Record<string, unknown>} [bodyJson] - JSON body to serialize and send.
   */
  static async request<T>({ method, url, bodyJson }: THomeserverRequestParams): Promise<T> {
    const owned = this.resolveOwnedSessionPath(url);

    // Handle owned session paths
    if (owned) {
      const { session, path } = owned;

      switch (method) {
        case HttpMethod.GET: {
          const response = await getOwnedResponse({ session, path, url });
          return (await parseResponseOrUndefined<T>({ response })) as T;
        }
        case HttpMethod.PUT:
          await session.storage
            .putJson(path, bodyJson ?? {})
            .catch((error) => handleError({ error, additionalContext: { url, method } }));
          return undefined as T;
        case HttpMethod.DELETE:
          await session.storage
            .delete(path)
            .catch((error) => handleError({ error, additionalContext: { url, method } }));
          return undefined as T;
      }
    }

    // Non-owned: only GET allowed on non-HTTP URLs
    if (method !== HttpMethod.GET && !isHttpUrl(url)) {
      throw Err.validation(
        ValidationErrorCode.INVALID_INPUT,
        `Authenticated writes must target an owned ${STORAGE_PATH_PREFIXES.join('* / ')}* path for the current session.`,
        {
          service: ErrorService.Homeserver,
          operation: 'request',
          context: { url, method, statusCode: HttpStatusCode.BAD_REQUEST },
        },
      );
    }

    // Handle public requests
    const pubkySdk = this.getPubkySdk();
    const fetchPromise =
      method === HttpMethod.GET
        ? isHttpUrl(url)
          ? pubkySdk.client.fetch(url)
          : pubkySdk.publicStorage.get(url as Address)
        : this.fetch({ url, options: { method, body: bodyJson ? JSON.stringify(bodyJson) : undefined } });

    const response = await fetchPromise.catch((error) => handleError({ error, additionalContext: { url, method } }));

    await assertOk({ response, url, operation: 'request' });

    return method === HttpMethod.GET ? ((await parseResponseOrUndefined<T>({ response })) as T) : (undefined as T);
  }

  /**
   * Uploads binary data to the homeserver using PUT.
   *
   * Intended for blob contents (e.g., avatars). Throws if the response is not OK.
   * Note: Uses `fetch` with `credentials: 'include'`.
   *
   * @param {string} url - Pubky URL.
   * @param {Uint8Array} blob - Raw bytes of the blob to upload.
   */
  static async putBlob({ url, blob }: TPutBlobParams) {
    const owned = this.resolveOwnedSessionPath(url);
    if (owned) {
      try {
        await owned.session.storage.putBytes(owned.path, blob);
        return;
      } catch (error) {
        return handleError({ error, additionalContext: { url, method: HttpMethod.PUT } });
      }
    }

    if (!isHttpUrl(url)) {
      throw Err.validation(
        ValidationErrorCode.INVALID_INPUT,
        `Blob uploads must target an owned ${STORAGE_PATH_PREFIXES.join('* / ')}* path for the current session.`,
        {
          service: ErrorService.Homeserver,
          operation: 'putBlob',
          context: { url, statusCode: HttpStatusCode.BAD_REQUEST },
        },
      );
    }

    const response = await this.fetch({ url, options: { method: HttpMethod.PUT, body: blob } });
    await assertOk({ response, url, operation: 'putBlob' });
  }

  /**
   * Lists files in a directory from the homeserver.
   *
   * Supports pagination with cursor and optional filtering.
   *
   * @param {string} baseDirectory - Base directory path to list files from.
   * @param {string} [cursor] - Optional cursor for pagination.
   * @param {boolean} [reverse=false] - Whether to list in reverse order.
   * @param {number} [limit=500] - Maximum number of files to return.
   * @returns {Promise<string[]>} Array of file URLs.
   */
  static async list({
    baseDirectory,
    cursor,
    reverse = false,
    limit = LIST_DEFAULT_LIMIT,
  }: THomeserverListParams): Promise<string[]> {
    const pubkySdk = this.getPubkySdk();
    try {
      const owned = this.resolveOwnedSessionPath(baseDirectory);
      if (owned) {
        const dirPath = owned.path.endsWith('/') ? owned.path : (`${owned.path}/` as PubPath<string>);
        const files = await owned.session.storage.list(dirPath, cursor ?? null, reverse, limit, false);
        Logger.debug('List successful', { baseDirectory, filesCount: files.length });
        return files;
      }

      const files = await pubkySdk.publicStorage.list(baseDirectory as Address, cursor ?? null, reverse, limit, false);
      Logger.debug('List successful', { baseDirectory, filesCount: files.length });
      return files;
    } catch (error) {
      // 404 here is not an error: missing directory means empty list. Bypass handleError to avoid Sentry capture.
      if (extractStatusCode(error) === HttpStatusCode.NOT_FOUND) {
        Logger.warn('[homeserver:list]', { outcome: 'fallback', reason: 'not_found', baseDirectory });
        return [];
      }
      return handleError({ error, additionalContext: { url: baseDirectory, baseDirectory } });
    }
  }

  /**
   * Lists ALL files under a base directory by paginating with a cursor until exhausted.
   *
   * A single `list` call is page-limited and must use a finite limit: non-finite values
   * (e.g. Infinity) coerce to 0 at the SDK's WASM boundary and silently return an empty
   * page. Paginating with the SDK cursor ("start after this URL") is the only reliable
   * way to enumerate an entire directory.
   *
   * @param {string} baseDirectory - Base directory path to list all files from.
   * @returns {Promise<string[]>} Array of every file URL under the directory.
   */
  static async listAll({ baseDirectory }: THomeserverListAllParams): Promise<string[]> {
    const files: string[] = [];
    let cursor: string | undefined;

    for (;;) {
      const batch = await this.list({ baseDirectory, cursor, limit: LIST_DEFAULT_LIMIT });
      files.push(...batch);

      if (batch.length < LIST_DEFAULT_LIMIT) {
        return files;
      }

      cursor = batch[batch.length - 1];
    }
  }

  /**
   * Deletes a file from the homeserver.
   *
   * @param {string} url - Pubky URL of the file to delete.
   */
  static async delete(url: string) {
    await this.request({ method: HttpMethod.DELETE, url });
    Logger.debug('Delete successful', { url });
  }

  /**
   * Deletes a homeserver resource for cleanup flows: 404 counts as success
   * ("already gone" is the desired end state) and transient failures retry
   * with a short backoff. Mirrors the hardened delete account deletion uses
   * (Sentry PUBKY-APP-6Y) so a single transport blip doesn't orphan resources.
   */
  static async deleteIdempotent(url: string) {
    for (let attempt = 1; ; attempt++) {
      try {
        await this.delete(url);
        return;
      } catch (error) {
        if (hasHttpStatus(error, HttpStatusCode.NOT_FOUND)) {
          return;
        }
        if (attempt >= DELETE_IDEMPOTENT_MAX_ATTEMPTS) {
          throw error;
        }
        Logger.warn('[HomeserverService] Delete failed, retrying', { url, attempt });
        await sleep(DELETE_IDEMPOTENT_RETRY_DELAY_MS * attempt);
      }
    }
  }

  /**
   * Fetches a resource from the homeserver.
   *
   * @param {string} url - Pubky URL to fetch.
   * @returns {Promise<Response>} The fetch response.
   */
  static async get(url: string): Promise<Response> {
    const pubkySdk = this.getPubkySdk();
    try {
      if (isHttpUrl(url)) {
        return await pubkySdk.client.fetch(url);
      }

      const owned = this.resolveOwnedSessionPath(url);
      if (owned) {
        return await getOwnedResponse({ session: owned.session, path: owned.path, url });
      }

      return await pubkySdk.publicStorage.get(url as Address);
    } catch (error) {
      return handleError({ error, additionalContext: { url, method: HttpMethod.GET } });
    }
  }

  /** Raw bytes of a resource (owned/public/http). Rejects (via `get`) on a missing or 4xx/5xx path. */
  static async getBytes(url: string): Promise<Uint8Array> {
    const response = await this.get(url);
    return new Uint8Array(await response.arrayBuffer());
  }

  /**
   * Bytes of an owned resource plus the server's `Last-Modified`, or null when it is absent (404) —
   * WITHOUT logging. For existence checks (e.g. unlock detection) where "not there" is an expected
   * outcome, not an error to report. Calls the SDK directly so a 404 bypasses `handleError`/Sentry,
   * mirroring `list`'s 404 fallback.
   *
   * Only a 404 means "absent": every other failure (403, 5xx, network) rejects, since the resource
   * may well exist and a null would let the caller record a missing file as a confirmed absence.
   *
   * `modifiedAt` is the homeserver's own write timestamp (`entry.modified_at`), so callers get an
   * ordering key the client cannot forge. `null` if the header is missing or unparseable.
   */
  static async getBytesIfExists(url: string): Promise<THomeserverBytesResult | null> {
    const owned = this.resolveOwnedSessionPath(url);
    // Unreadable without a session — return null rather than fire an unauthenticated request.
    if (!owned) return null;
    try {
      // `storage.get` resolves for any status, so the response has to be checked here.
      const response = await owned.session.storage.get(owned.path);
      if (response.status === HttpStatusCode.NOT_FOUND) return null;
      await assertOk({ response, url, operation: 'getBytesIfExists' });
      const lastModified = Date.parse(response.headers.get('last-modified') ?? '');
      return {
        bytes: new Uint8Array(await response.arrayBuffer()),
        modifiedAt: Number.isNaN(lastModified) ? null : lastModified,
      };
    } catch (error) {
      if (extractStatusCode(error) === HttpStatusCode.NOT_FOUND) return null;
      return handleError({ error, additionalContext: { url, method: HttpMethod.GET } });
    }
  }

  /**
   * Checks whether a homeserver resource exists without treating an expected 404 as an error.
   * Storage SDK probes return a boolean; plain HTTP URLs retain explicit response handling.
   */
  static async exists(url: string): Promise<boolean> {
    const pubkySdk = this.getPubkySdk();

    try {
      if (isHttpUrl(url)) {
        const response = await pubkySdk.client.fetch(url);
        if (response.status === HttpStatusCode.NOT_FOUND) return false;
        await assertOk({ response, url, operation: 'exists' });
        return true;
      }

      const owned = this.resolveOwnedSessionPath(url);
      return owned
        ? await owned.session.storage.exists(owned.path)
        : await pubkySdk.publicStorage.exists(url as Address);
    } catch (error) {
      return handleError({
        error,
        additionalContext: { url, method: HttpMethod.GET, operation: 'exists' },
      });
    }
  }

  /** Keep delegated grant keys in the SDK's IndexedDB, never in app storage. */
  static async saveSession(session: Session): Promise<string> {
    const store = this.getPubkySdk().browserSessionStore;
    try {
      const saved = await store.save(session);
      try {
        return saved.id;
      } finally {
        saved.free();
      }
    } catch (error) {
      return handleError({ error, additionalContext: { operation: 'saveSession' } });
    } finally {
      store.free();
    }
  }

  static async restoreStoredSession(id: string): Promise<Session> {
    const store = this.getPubkySdk().browserSessionStore;
    try {
      return await store.restore(id);
    } catch (error) {
      return handleError({ error, additionalContext: { operation: 'restoreStoredSession' } });
    } finally {
      store.free();
    }
  }

  static async removeStoredSession(id: string): Promise<void> {
    const store = this.getPubkySdk().browserSessionStore;
    try {
      await store.remove(id);
    } catch (error) {
      return handleError({ error, additionalContext: { operation: 'removeStoredSession' } });
    } finally {
      store.free();
    }
  }

  /**
   * Restore an authenticated Session from a previous `session.export()` snapshot.
   */
  static async restoreSession({ sessionExport }: THomeserverRestoreSessionParams): Promise<Session> {
    try {
      const pubkySdk = this.getPubkySdk();
      return await pubkySdk.restoreSession(sessionExport);
    } catch (error) {
      return handleError({
        error,
        additionalContext: { sessionExport: Boolean(sessionExport), operation: 'restoreSession' },
      });
    }
  }

  /**
   * Generates a signup token for dev/test environments.
   * Calls the server-side API route to keep admin credentials secure.
   *
   * @security Admin credentials are never exposed to the client.
   * The actual token generation happens server-side via /api/dev/signup-token.
   */
  static async generateSignupToken() {
    // Allow in development or when Cypress is running (for E2E tests in production builds)
    const isCypressRunning = typeof window !== 'undefined' && 'Cypress' in window;
    const isProduction = process.env.NODE_ENV === 'production';

    if (isProduction && !isCypressRunning) {
      throw Err.validation(
        ValidationErrorCode.INVALID_INPUT,
        'generateSignupToken is only available in non-production environments.',
        {
          service: ErrorService.Homeserver,
          operation: 'generateSignupToken',
          context: { isProduction, isCypressRunning },
        },
      );
    }

    // Call server-side API route to generate token (keeps admin credentials secure)
    const response = await fetch('/api/dev/signup-token', {
      method: 'GET',
    });

    if (!response.ok) {
      throw httpResponseToError(response, ErrorService.Homeserver, 'generateSignupToken', '/api/dev/signup-token');
    }

    const data = await response.json();
    if (!data.token) {
      throw Err.server(ServerErrorCode.UNKNOWN_ERROR, 'No token received from server', {
        service: ErrorService.Homeserver,
        operation: 'generateSignupToken',
      });
    }

    return data.token;
  }

  /**
   * Subscribe to homeserver `/events-stream` for a user's pub directory subtree (SDK SSE wrapper).
   * Used for mute-list sync; callers own {@link ReadableStreamDefaultReader} lifecycle.
   */
  static async subscribeUserEventStreamForPath(params: {
    userZ32: TPubkyModel;
    cursor: string | null;
    pathPrefix: string;
  }): Promise<ReadableStream<THomeserverUserEvent>> {
    try {
      const pubkySdk = this.getPubkySdk();
      const pk = PublicKey.from(params.userZ32);
      const stream = (await pubkySdk
        .eventStreamForUser(pk, params.cursor)
        .path(params.pathPrefix)
        .live()
        .subscribe()) as ReadableStream<HomeserverSdkUserEvent>;

      return this.normalizeUserEventStream(stream);
    } catch (error) {
      // `operation` is matched by the `homeserver-event-stream-connect` Sentry drop rule:
      // the mute-list coordinator reconnects on connect failures by design.
      return handleError({
        error,
        additionalContext: { pathPrefix: params.pathPrefix, operation: HOMESERVER_EVENT_STREAM_SUBSCRIBE_OPERATION },
      });
    }
  }

  private static normalizeUserEventStream(
    stream: ReadableStream<HomeserverSdkUserEvent>,
  ): ReadableStream<THomeserverUserEvent> {
    const reader = stream.getReader();

    return new ReadableStream<THomeserverUserEvent>({
      async pull(controller) {
        const { done, value } = await reader.read();

        if (done) {
          controller.close();
          return;
        }

        try {
          controller.enqueue({
            cursor: value.cursor,
            eventType: value.eventType,
          });
        } finally {
          try {
            value.free();
          } catch {
            // Ignore WASM dispose errors.
          }
        }
      },
      async cancel(reason) {
        await reader.cancel(reason).catch(() => {});
      },
    });
  }
}
